import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { MonitorSmartphone } from 'lucide-react';
import { PAGE, PORTAL_NAME, SHIFT, formatTimeInTimezone, useTimezone } from '@kioskgaming/ui';
import CashierLayout from '../components/CashierLayout';
import PairingCodeForm from '../components/PairingCodeForm';
import { useCashierAuth } from '../hooks/useCashierAuth';
import { cashierCheckInShift, CheckInResult } from '../services/cashierApi';
import { describeDeviceError } from '../utils/deviceErrors';
import { toStartScreen } from '../utils/startReadiness';

/** While waiting for approval, GET /cashier/me is polled this often (the auth provider also refreshes on focus). */
export const APPROVAL_POLL_MS = 5000;

const TONE: Record<string, string> = {
  waiting_approval: 'border-amber-200 bg-amber-50 text-amber-900',
  mismatch: 'border-amber-200 bg-amber-50 text-amber-900',
  busy: 'border-amber-200 bg-amber-50 text-amber-900',
  blocked: 'border-rose-200 bg-rose-50 text-rose-900',
  unknown_device: 'border-rose-200 bg-rose-50 text-rose-900',
};

export default function StartShiftPage() {
  const { id } = useParams<{ id: string }>();
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const { device, refreshMe, setDevice, logout } = useCashierAuth();
  const [result, setResult] = useState<CheckInResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const mounted = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const checkIn = useCallback(async () => {
    if (!id) return;
    const seq = ++requestSeq.current;
    setChecking(true);
    const res = await cashierCheckInShift(id);
    // Only the latest answer counts, and never after leaving the page.
    if (!mounted.current || seq !== requestSeq.current) return;
    setChecking(false);

    if (!res.success || !res.data) {
      if (res.code === 'INVALID_SHIFT_STATE') {
        if (res.currentStatus === 'active') {
          navigate(`/shifts/${id}`, { replace: true });
          return;
        }
        toast.error(describeDeviceError(res.code)!.message);
        navigate('/', { replace: true });
        return;
      }
      toast.error(res.message || 'Could not check in to this shift');
      setLoadError(res.message || 'Could not check in to this shift.');
      return;
    }

    setLoadError(null);
    setResult(res.data);
    // Keep "This device" in sync with what the server just said about it.
    setDevice(res.data.device);
    if (toStartScreen(res.data).state === 'ready') {
      navigate(`/shifts/${id}/opening`, { replace: true });
    }
  }, [id, navigate, setDevice]);

  useEffect(() => {
    void checkIn();
  }, [checkIn]);

  const screen = result ? toStartScreen(result) : null;
  const waiting = screen?.state === 'waiting_approval';

  // Waiting for approval: poll the device status.
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => {
      void refreshMe();
    }, APPROVAL_POLL_MS);
    return () => window.clearInterval(timer);
  }, [waiting, refreshMe]);

  // The device status changed while waiting — approved in the Shop portal, approved with a pairing code
  // (the form updates the auth device), or rejected: check in again, which continues or shows the reason.
  const deviceStatus = device?.status;
  useEffect(() => {
    if (waiting && deviceStatus && deviceStatus !== 'pending_approval') void checkIn();
  }, [waiting, deviceStatus, checkIn]);

  if (!id) return <Navigate to="/" replace />;

  const backToShifts = (
    <button
      type="button"
      onClick={() => navigate('/', { replace: true })}
      className="rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-stone-50"
    >
      Back to {PAGE.myShifts}
    </button>
  );

  let body;
  if (loadError) {
    body = (
      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
        <p className="text-sm text-stone-700">{loadError}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void checkIn()}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Try again
          </button>
          {backToShifts}
        </div>
      </div>
    );
  } else if (!screen || screen.state === 'ready') {
    body = <div className="text-sm text-stone-600">{screen ? 'Opening cash count…' : 'Checking this device…'}</div>;
  } else {
    body = (
      <div className={`rounded-xl border p-4 sm:p-6 ${TONE[screen.state]}`}>
        <div className="flex items-start gap-3">
          <MonitorSmartphone className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{screen.title}</h2>
            <p className="mt-1 text-sm">{screen.message}</p>
          </div>
        </div>

        {waiting ? (
          <div className="mt-4 space-y-4">
            <div className="rounded-lg bg-white/70 p-4 text-center">
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Device code</div>
              <div className="mt-1 font-mono text-4xl font-bold tracking-[0.3em] text-stone-900" data-testid="device-short-code">
                {screen.shortCode || '—'}
              </div>
              <div className="mt-1 text-xs text-amber-800">Your manager sees the same code in the {PORTAL_NAME.shop}.</div>
            </div>
            <PairingCodeForm />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void checkIn()}
                disabled={checking}
                className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
              >
                {checking ? 'Checking…' : 'Check again'}
              </button>
              {backToShifts}
            </div>
            <p className="text-xs text-amber-800">This page continues by itself once the device is approved.</p>
          </div>
        ) : screen.action === 'sign_in_again' ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/login', { replace: true });
              }}
              className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
            >
              Sign in again
            </button>
          </div>
        ) : (
          <div className="mt-4">{backToShifts}</div>
        )}
      </div>
    );
  }

  return (
    <CashierLayout
      title={SHIFT.startShift}
      subtitle={screen?.checkedInAt ? `Checked in at ${formatTimeInTimezone(screen.checkedInAt, timezone)}` : undefined}
      backTo="/"
    >
      {body}
    </CashierLayout>
  );
}
